/*!
 * Bombka Wspomnień – autonomous scene engine
 * One lightweight canvas renderer used for:
 *   - the persistent full-page background (snow in 3 depths, bokeh, twinkling
 *     stars, sparks travelling along curved paths with light trails)
 *   - the hero foreground layer (a few near-camera flakes/sparks that pass in
 *     front of the product but fade out around it so it always stays visible)
 * Pre-rendered sprites + drawImage keep it cheap. Particle counts adapt to the
 * device tier and are reduced automatically if frames get slow.
 */
(function () {
  'use strict';

  var html = document.documentElement;
  var TIER = (window.BW && window.BW.tier) || 'high';
  var REDUCED = TIER === 'reduced';

  var PRESETS = {
    back: {
      high:    { snow: 150, bokeh: 20, spark: 16, stars: 70 },
      mid:     { snow: 85,  bokeh: 12, spark: 9,  stars: 40 },
      low:     { snow: 45,  bokeh: 7,  spark: 4,  stars: 22 },
      reduced: { snow: 26,  bokeh: 8,  spark: 0,  stars: 24 }
    },
    front: {
      high:    { snow: 14, bokeh: 4, spark: 6, stars: 0 },
      mid:     { snow: 8,  bokeh: 2, spark: 3, stars: 0 },
      low:     { snow: 0,  bokeh: 0, spark: 0, stars: 0 },
      reduced: { snow: 0,  bokeh: 0, spark: 0, stars: 0 }
    }
  };

  /* ---------- sprites ---------- */
  function sprite(size, stops, ring) {
    var c = document.createElement('canvas');
    c.width = c.height = size;
    var g = c.getContext('2d');
    var h = size / 2;
    var gr = g.createRadialGradient(h, h, 0, h, h, h);
    for (var i = 0; i < stops.length; i++) gr.addColorStop(stops[i][0], stops[i][1]);
    g.fillStyle = gr;
    g.beginPath(); g.arc(h, h, h, 0, Math.PI * 2); g.fill();
    if (ring) {
      g.strokeStyle = ring;
      g.lineWidth = size * 0.025;
      g.beginPath(); g.arc(h, h, h * 0.86, 0, Math.PI * 2); g.stroke();
    }
    return c;
  }

  var SPR = null;
  function sprites() {
    if (SPR) return SPR;
    SPR = {
      snow: sprite(32, [[0, 'rgba(255,255,255,1)'], [0.35, 'rgba(255,250,240,0.85)'], [1, 'rgba(255,250,240,0)']]),
      snowSoft: sprite(64, [[0, 'rgba(255,248,235,0.75)'], [0.45, 'rgba(255,245,230,0.35)'], [1, 'rgba(255,245,230,0)']]),
      spark: sprite(32, [[0, 'rgba(255,255,255,1)'], [0.2, 'rgba(255,230,170,0.95)'], [0.5, 'rgba(255,190,90,0.35)'], [1, 'rgba(255,170,60,0)']]),
      bokeh: {
        w: sprite(128, [[0, 'rgba(255,226,170,0.20)'], [0.72, 'rgba(255,214,150,0.24)'], [0.88, 'rgba(255,226,175,0.34)'], [1, 'rgba(255,214,150,0)']], 'rgba(255,236,200,0.18)'),
        g: sprite(128, [[0, 'rgba(255,196,96,0.24)'], [0.7, 'rgba(240,180,80,0.26)'], [0.9, 'rgba(255,205,120,0.34)'], [1, 'rgba(255,190,90,0)']], 'rgba(255,214,140,0.16)'),
        r: sprite(128, [[0, 'rgba(220,60,70,0.20)'], [0.72, 'rgba(200,50,60,0.22)'], [0.9, 'rgba(240,90,90,0.3)'], [1, 'rgba(200,50,60,0)']]),
        e: sprite(128, [[0, 'rgba(90,170,120,0.16)'], [0.72, 'rgba(70,150,100,0.18)'], [0.9, 'rgba(120,200,150,0.24)'], [1, 'rgba(70,150,100,0)']])
      }
    };
    return SPR;
  }

  function rand(a, b) { return a + Math.random() * (b - a); }
  function pick(arr) { return arr[(Math.random() * arr.length) | 0]; }
  function smooth(e0, e1, x) { var t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0))); return t * t * (3 - 2 * t); }

  /* ---------- scene ---------- */
  function Scene(canvas, mode) {
    this.canvas = canvas;
    this.mode = mode;
    this.ctx = canvas.getContext('2d');
    this.fixed = mode === 'back';
    this.counts = Object.assign({}, PRESETS[mode][TIER] || PRESETS[mode].mid);
    this.running = false;
    this.visible = true;
    this.t = 0;
    this.last = 0;
    this.frameAvg = 16;
    this.frames = 0;
    this.degraded = 0;
    this.minFrame = TIER === 'low' ? 1000 / 32 : 0;
    this.dprCap = TIER === 'high' ? 1.6 : TIER === 'mid' ? 1.3 : 1;
    this.w = 0; this.h = 0;
    this.resize(true);
    this.build();
    this.bind();
  }

  Scene.prototype.resize = function (force) {
    var w, h;
    if (this.fixed) { w = window.innerWidth; h = window.innerHeight; }
    else { var r = this.canvas.parentNode.getBoundingClientRect(); w = r.width; h = r.height; }
    w = Math.max(1, Math.round(w)); h = Math.max(1, Math.round(h));
    // Ignore small height-only changes (mobile toolbar show/hide) to avoid thrashing.
    if (!force && w === this.w && Math.abs(h - this.h) < 120) return;
    var sx = this.w ? w / this.w : 1, sy = this.h ? h / this.h : 1;
    var dpr = Math.min(window.devicePixelRatio || 1, this.dprCap);
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.canvas.style.width = w + 'px';
    this.canvas.style.height = h + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (this.snow && (sx !== 1 || sy !== 1)) {
      var all = [].concat(this.snow, this.bokeh, this.stars);
      for (var i = 0; i < all.length; i++) {
        var p = all[i];
        if (p.x != null) { p.x *= sx; p.y *= sy; }
        if (p.bx != null) { p.bx *= sx; p.by *= sy; }
      }
    }
    this.w = w; this.h = h;
    if (!this.running && this.snow) this.draw();
  };

  Scene.prototype.build = function () {
    var w = this.w, h = this.h, c = this.counts, front = this.mode === 'front';
    var i;
    // Fewer particles on narrow screens even within a tier.
    var areaScale = Math.min(1, Math.max(0.45, (w * h) / (1440 * 900)));
    if (!front) areaScale = Math.max(areaScale, 0.55);

    this.snow = [];
    for (i = 0; i < Math.round(c.snow * areaScale); i++) this.snow.push(this.newFlake(true));

    this.bokeh = [];
    var kinds = ['w', 'w', 'g', 'g', 'g', 'r', 'e'];
    for (i = 0; i < Math.round(c.bokeh * areaScale + (front ? 0 : 2)); i++) {
      var r = front ? rand(50, 120) : rand(18, 85);
      this.bokeh.push({
        bx: rand(-0.05, 1.05) * w, by: rand(-0.05, 1.05) * h,
        ax: rand(20, 90), ay: rand(14, 60),
        fx: rand(0.02, 0.07), fy: rand(0.02, 0.06),
        ph: rand(0, 6.28), ph2: rand(0, 6.28),
        af: rand(0.15, 0.5), a: front ? rand(0.25, 0.5) : rand(0.35, 0.95),
        r: r, kind: pick(kinds)
      });
    }

    this.stars = [];
    for (i = 0; i < Math.round(c.stars * areaScale); i++) {
      this.stars.push({ x: rand(0, w), y: rand(0, h * 0.75), r: rand(0.4, 1.3), ph: rand(0, 6.28), f: rand(0.4, 1.6) });
    }

    this.sparks = [];
    for (i = 0; i < c.spark; i++) this.sparks.push(this.newSpark(true));
  };

  Scene.prototype.newFlake = function (initial) {
    var front = this.mode === 'front';
    var z = front ? rand(0.75, 1) : Math.pow(Math.random(), 1.6); // more far flakes than near
    return {
      x: rand(-20, this.w + 20),
      y: initial ? rand(-20, this.h) : rand(-60, -10),
      z: z,
      r: front ? rand(3.5, 7.5) : 0.7 + z * 2.4,
      vy: front ? rand(38, 70) : 10 + z * 38,
      amp: rand(6, 22) * (0.4 + z),
      fq: rand(0.3, 1.1),
      ph: rand(0, 6.28),
      soft: front || z > 0.82
    };
  };

  Scene.prototype.newSpark = function (initial) {
    var w = this.w, h = this.h;
    // Curved cubic path drifting upward like a warm ember / light trail.
    var x0 = rand(0.05, 0.95) * w, y0 = h * rand(0.55, 1.05);
    var x3 = x0 + rand(-0.35, 0.35) * w, y3 = h * rand(-0.1, 0.35);
    return {
      p: [x0, y0, x0 + rand(-0.3, 0.3) * w, y0 - rand(0.1, 0.4) * h, x3 + rand(-0.3, 0.3) * w, y3 + rand(0.1, 0.35) * h, x3, y3],
      life: initial ? Math.random() : 0,
      dur: rand(7, 14),
      size: rand(1.2, 2.6),
      trail: [],
      delay: initial ? 0 : rand(0, 3)
    };
  };

  Scene.prototype.bez = function (p, t) {
    var u = 1 - t, a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, d = t * t * t;
    return [a * p[0] + b * p[2] + c * p[4] + d * p[6], a * p[1] + b * p[3] + c * p[5] + d * p[7]];
  };

  Scene.prototype.wind = function (t) {
    return Math.sin(t * 0.07) * 14 + Math.sin(t * 0.23 + 1.3) * 7 + Math.sin(t * 0.011) * 10;
  };

  Scene.prototype.step = function (dt) {
    var t = (this.t += dt), w = this.w, h = this.h, i, p;
    var wind = this.wind(t);
    for (i = 0; i < this.snow.length; i++) {
      p = this.snow[i];
      p.y += p.vy * dt;
      p.x += (Math.sin(t * p.fq + p.ph) * p.amp * 0.6 + wind * (0.3 + p.z)) * dt;
      if (p.y > h + 20 || p.x < -40 || p.x > w + 40) {
        var n = this.newFlake(false);
        if (p.x < -40) n.x = w + 20; else if (p.x > w + 40) n.x = -20;
        this.snow[i] = n;
      }
    }
    for (i = 0; i < this.sparks.length; i++) {
      p = this.sparks[i];
      if (p.delay > 0) { p.delay -= dt; continue; }
      p.life += dt / p.dur;
      if (p.life >= 1) { this.sparks[i] = this.newSpark(false); continue; }
      var pos = this.bez(p.p, p.life);
      p.trail.push(pos);
      if (p.trail.length > 14) p.trail.shift();
    }
  };

  Scene.prototype.draw = function () {
    var ctx = this.ctx, t = this.t, s = sprites(), i, p, a, sz;
    var front = this.mode === 'front';
    var w = this.w, h = this.h;
    // product "keep clear" zone for the foreground layer
    var cx = w * 0.5, cy = h * 0.5, cr = Math.min(w, h) * 0.34;

    ctx.clearRect(0, 0, w, h);

    // stars
    if (this.stars.length) {
      for (i = 0; i < this.stars.length; i++) {
        p = this.stars[i];
        a = 0.5 + 0.5 * Math.sin(t * p.f + p.ph);
        a = 0.12 + 0.8 * a * a * a;
        ctx.globalAlpha = a * 0.85;
        sz = p.r * 3;
        ctx.drawImage(s.spark, p.x - sz, p.y - sz, sz * 2, sz * 2);
      }
    }

    // bokeh (additive)
    ctx.globalCompositeOperation = 'lighter';
    for (i = 0; i < this.bokeh.length; i++) {
      p = this.bokeh[i];
      var x = p.bx + Math.sin(t * p.fx * 6.28 + p.ph) * p.ax;
      var y = p.by + Math.cos(t * p.fy * 6.28 + p.ph2) * p.ay;
      a = p.a * (0.55 + 0.45 * Math.sin(t * p.af + p.ph2));
      if (front) a *= smooth(cr * 0.7, cr * 1.4, Math.hypot(x - cx, y - cy));
      if (a < 0.01) continue;
      ctx.globalAlpha = a;
      ctx.drawImage(s.bokeh[p.kind], x - p.r, y - p.r, p.r * 2, p.r * 2);
    }

    // sparks with light trails
    for (i = 0; i < this.sparks.length; i++) {
      p = this.sparks[i];
      if (p.delay > 0 || p.trail.length < 2) continue;
      var env = Math.sin(Math.PI * p.life);
      var tr = p.trail;
      for (var k = 1; k < tr.length; k++) {
        var f = k / tr.length;
        var fa = env * f * 0.55;
        if (front) fa *= smooth(cr * 0.6, cr * 1.2, Math.hypot(tr[k][0] - cx, tr[k][1] - cy));
        ctx.globalAlpha = fa;
        ctx.strokeStyle = 'rgba(255,214,150,1)';
        ctx.lineWidth = p.size * f;
        ctx.beginPath();
        ctx.moveTo(tr[k - 1][0], tr[k - 1][1]);
        ctx.lineTo(tr[k][0], tr[k][1]);
        ctx.stroke();
      }
      var head = tr[tr.length - 1];
      a = env;
      if (front) a *= smooth(cr * 0.6, cr * 1.2, Math.hypot(head[0] - cx, head[1] - cy));
      ctx.globalAlpha = a;
      sz = p.size * 5;
      ctx.drawImage(s.spark, head[0] - sz, head[1] - sz, sz * 2, sz * 2);
    }

    // snow
    ctx.globalCompositeOperation = 'source-over';
    for (i = 0; i < this.snow.length; i++) {
      p = this.snow[i];
      a = front ? 0.55 : 0.28 + p.z * 0.62;
      if (front) a *= smooth(cr * 0.75, cr * 1.35, Math.hypot(p.x - cx, p.y - cy));
      if (a < 0.02) continue;
      ctx.globalAlpha = a;
      sz = p.r * (p.soft ? 2.6 : 1.6);
      ctx.drawImage(p.soft ? s.snowSoft : s.snow, p.x - sz, p.y - sz, sz * 2, sz * 2);
    }
    ctx.globalAlpha = 1;
  };

  Scene.prototype.loop = function (now) {
    if (!this.running) return;
    var self = this;
    this.raf = requestAnimationFrame(function (n) { self.loop(n); });
    if (!this.last) { this.last = now; return; }
    var elapsed = now - this.last;
    if (elapsed < this.minFrame) return;
    this.last = now;
    var dt = Math.min(0.05, elapsed / 1000);
    this.step(dt);
    this.draw();
    this.monitor(elapsed);
  };

  // Adaptive quality: if frames are consistently slow, thin out particles.
  Scene.prototype.monitor = function (elapsed) {
    this.frameAvg = this.frameAvg * 0.95 + elapsed * 0.05;
    this.frames++;
    if (this.frames > 150 && this.frameAvg > 24 && this.degraded < 3) {
      this.degraded++;
      this.frames = 0;
      var cut = function (arr) { return arr.slice(0, Math.ceil(arr.length * 0.65)); };
      this.snow = cut(this.snow);
      this.bokeh = cut(this.bokeh);
      this.sparks = cut(this.sparks);
      this.stars = cut(this.stars);
      if (this.degraded >= 2) this.minFrame = 1000 / 32;
      if (this.mode === 'back' && this.degraded >= 2) html.classList.add('bw-perf-low');
    }
  };

  Scene.prototype.start = function () {
    if (this.running || REDUCED) return;
    this.running = true;
    this.last = 0;
    var self = this;
    this.raf = requestAnimationFrame(function (n) { self.loop(n); });
  };

  Scene.prototype.stop = function () {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
  };

  Scene.prototype.bind = function () {
    var self = this, rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(function () { self.resize(false); }, 150);
    }, { passive: true });

    if (!this.fixed && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        if (self.visible && !document.hidden) self.start(); else self.stop();
      }, { rootMargin: '80px' }).observe(this.canvas);
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.stop();
      else if (self.visible) self.start();
    });
  };

  /* ==========================================================
     StarField — site-wide background: tiny stars that gently
     drift and twinkle, a few brighter cross-glint stars in
     Christmas colours, and an occasional shooting star.
     ========================================================== */
  var STAR_COUNTS = { high: 260, mid: 190, low: 130, reduced: 160 };
  var STAR_COLORS = [
    ['255,247,232', 46], // warm white
    ['255,226,160', 22], // gold
    ['226,236,255', 18], // icy blue-white
    ['255,170,160', 7],  // soft red
    ['170,240,195', 7]   // soft green
  ];

  function pickColor() {
    var total = 0, i;
    for (i = 0; i < STAR_COLORS.length; i++) total += STAR_COLORS[i][1];
    var r = Math.random() * total;
    for (i = 0; i < STAR_COLORS.length; i++) {
      r -= STAR_COLORS[i][1];
      if (r <= 0) return STAR_COLORS[i][0];
    }
    return STAR_COLORS[0][0];
  }

  var STAR_SPR = {};
  function starSprite(rgb) {
    if (STAR_SPR[rgb]) return STAR_SPR[rgb];
    STAR_SPR[rgb] = sprite(32, [[0, 'rgba(255,255,255,1)'], [0.18, 'rgba(' + rgb + ',0.95)'], [0.45, 'rgba(' + rgb + ',0.28)'], [1, 'rgba(' + rgb + ',0)']]);
    return STAR_SPR[rgb];
  }

  function StarField(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.running = false;
    this.visible = true;
    this.t = 0;
    this.last = 0;
    this.frameAvg = 16;
    this.frames = 0;
    this.degraded = 0;
    this.minFrame = TIER === 'low' ? 1000 / 30 : TIER === 'mid' ? 1000 / 45 : 0;
    this.dprCap = TIER === 'high' ? 2 : TIER === 'mid' ? 1.5 : 1.25;
    this.w = 0; this.h = 0;
    this.shoot = null;
    this.nextShoot = rand(4, 9);
    this.resize(true);
    this.build();
    this.bind();
  }

  StarField.prototype.resize = Scene.prototype.resize;

  StarField.prototype.build = function () {
    var w = this.w, h = this.h;
    // keep a rich sky on phones too (stars are very cheap to draw)
    var area = Math.min(1.2, Math.max(0.75, (w * h) / (1440 * 900)));
    var n = Math.round((STAR_COUNTS[TIER] || 160) * area);
    this.stars = [];
    for (var i = 0; i < n; i++) {
      var z = Math.pow(Math.random(), 2.2); // most stars are far and tiny
      var bright = Math.random() < 0.06;
      var ang = rand(0, Math.PI * 2);
      var speed = 1.5 + z * 6; // px per second – a very gentle drift
      this.stars.push({
        x: rand(0, w), y: rand(0, h),
        r: bright ? rand(1.1, 1.7) : 0.3 + z * 1.05,
        z: z,
        vx: Math.cos(ang) * speed * 0.6 + 1.2, // slight common breeze to the right
        vy: Math.sin(ang) * speed * 0.35 - 0.6,
        wa: rand(3, 12) * (0.4 + z), wf: rand(0.05, 0.18), wp: rand(0, 6.28),
        tf: rand(0.4, 1.6), tp: rand(0, 6.28),
        base: bright ? rand(0.75, 1) : rand(0.35, 0.9),
        bright: bright,
        rgb: bright ? pick(['255,236,190', '255,247,232', '255,190,180', '190,245,210']) : pickColor()
      });
    }
    // Scene.prototype.resize rescales snow/bokeh/stars on resize
    this.snow = []; this.bokeh = [];
  };

  StarField.prototype.spawnShoot = function () {
    var w = this.w, h = this.h;
    var fromLeft = Math.random() < 0.5;
    var x = fromLeft ? rand(0.05, 0.45) * w : rand(0.55, 0.95) * w;
    var y = rand(0.04, 0.32) * h;
    var len = Math.min(w, h) * rand(0.25, 0.4);
    var dir = fromLeft ? 1 : -1;
    this.shoot = { x: x, y: y, dx: dir * len, dy: len * rand(0.28, 0.45), life: 0, dur: rand(0.9, 1.4) };
  };

  StarField.prototype.step = function (dt) {
    var t = (this.t += dt), w = this.w, h = this.h, s, i;
    for (i = 0; i < this.stars.length; i++) {
      s = this.stars[i];
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      if (s.x < -10) s.x += w + 20; else if (s.x > w + 10) s.x -= w + 20;
      if (s.y < -10) s.y += h + 20; else if (s.y > h + 10) s.y -= h + 20;
    }
    if (TIER === 'high' || TIER === 'mid') {
      if (this.shoot) {
        this.shoot.life += dt / this.shoot.dur;
        if (this.shoot.life >= 1) { this.shoot = null; this.nextShoot = t + rand(8, 16); }
      } else if (t >= this.nextShoot) {
        this.spawnShoot();
      }
    }
  };

  StarField.prototype.draw = function () {
    var ctx = this.ctx, t = this.t, w = this.w, h = this.h, i, s, a, sz, x, y;
    ctx.clearRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'lighter';
    for (i = 0; i < this.stars.length; i++) {
      s = this.stars[i];
      x = s.x + Math.sin(t * s.wf * 6.28 + s.wp) * s.wa;
      y = s.y + Math.cos(t * s.wf * 5.1 + s.wp) * s.wa * 0.6;
      var tw = 0.5 + 0.5 * Math.sin(t * s.tf * 6.28 * 0.35 + s.tp);
      a = s.base * (0.35 + 0.65 * tw * tw);
      // fade towards the treeline so stars never sit on top of the forest
      if (y > h * 0.8) a *= Math.max(0, 1 - (y - h * 0.8) / (h * 0.15));
      if (a < 0.02) continue;
      ctx.globalAlpha = a;
      sz = s.r * (s.bright ? 4.2 : 3);
      ctx.drawImage(starSprite(s.rgb), x - sz, y - sz, sz * 2, sz * 2);
      if (s.bright) {
        // 4-point Christmas-star glint
        var L = s.r * (5 + 6 * tw);
        ctx.globalAlpha = a * 0.55;
        ctx.strokeStyle = 'rgba(' + s.rgb + ',1)';
        ctx.lineWidth = 0.7;
        ctx.beginPath();
        ctx.moveTo(x - L, y); ctx.lineTo(x + L, y);
        ctx.moveTo(x, y - L); ctx.lineTo(x, y + L);
        ctx.stroke();
        ctx.globalAlpha = a * 0.25;
        L *= 0.45;
        ctx.beginPath();
        ctx.moveTo(x - L, y - L); ctx.lineTo(x + L, y + L);
        ctx.moveTo(x - L, y + L); ctx.lineTo(x + L, y - L);
        ctx.stroke();
      }
    }
    var sh = this.shoot;
    if (sh) {
      var p = sh.life, env = Math.sin(Math.PI * p);
      var hx = sh.x + sh.dx * p, hy = sh.y + sh.dy * p;
      var tx = hx - sh.dx * 0.28, ty = hy - sh.dy * 0.28;
      var g = ctx.createLinearGradient(tx, ty, hx, hy);
      g.addColorStop(0, 'rgba(255,226,170,0)');
      g.addColorStop(1, 'rgba(255,240,210,' + (0.85 * env).toFixed(3) + ')');
      ctx.globalAlpha = 1;
      ctx.strokeStyle = g;
      ctx.lineWidth = 1.4;
      ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(hx, hy); ctx.stroke();
      ctx.globalAlpha = env;
      ctx.drawImage(starSprite('255,240,210'), hx - 5, hy - 5, 10, 10);
    }
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
  };

  StarField.prototype.loop = Scene.prototype.loop;
  StarField.prototype.start = Scene.prototype.start;
  StarField.prototype.stop = Scene.prototype.stop;
  StarField.prototype.bind = Scene.prototype.bind;
  StarField.prototype.monitor = function (elapsed) {
    this.frameAvg = this.frameAvg * 0.95 + elapsed * 0.05;
    this.frames++;
    if (this.frames > 150 && this.frameAvg > 24 && this.degraded < 3) {
      this.degraded++;
      this.frames = 0;
      this.stars = this.stars.slice(0, Math.ceil(this.stars.length * 0.65));
      if (this.degraded >= 2) { this.minFrame = 1000 / 30; html.classList.add('bw-perf-low'); }
    }
  };
  StarField.prototype.fixed = true;
  StarField.prototype.mode = 'back';

  /* ---------- boot ---------- */
  var scenes = [];
  function boot() {
    var nodes = document.querySelectorAll('canvas[data-bw-scene]');
    for (var i = 0; i < nodes.length; i++) {
      var c = nodes[i];
      if (c.__bwScene) continue;
      var mode = c.getAttribute('data-bw-scene') === 'front' ? 'front' : 'back';
      var preset = PRESETS[mode][TIER];
      if (!preset || (mode === 'front' && !preset.snow && !preset.spark)) { c.style.display = 'none'; continue; }
      var sc = mode === 'back' ? new StarField(c) : new Scene(c, mode);
      c.__bwScene = sc;
      scenes.push(sc);
      if (REDUCED) {
        // Reduced motion: one calm, static frame — no movement.
        sc.step(0.001); sc.draw();
      } else {
        sc.start();
      }
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);

  window.BWScene = { scenes: scenes, boot: boot };
})();
